import { useRef, useState } from 'react';
import { Upload, FileDown, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const TEMPLATE_HEADERS = ['customer_name', 'vehicle', 'phone', 'email', 'stage', 'priority', 'estimated_value', 'next_step', 'notes'];
const SAMPLE_ROW = ['Jane Doe', '2024 Land Rover Defender', '+15195550101', 'jane@example.com', 'new', 'warm', '85000', 'Call back Friday 2pm', 'Trade-in: 2020 RR Sport'];
const VALID_STAGES = new Set(['new', 'contacted', 'demo', 'quote', 'negotiation', 'sold', 'lost']);
const VALID_PRIORITY = new Set(['hot', 'warm', 'cold']);

function parseCsv(text: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  if (lines.length < 2) return [];
  const parseLine = (line: string) => {
    const out: string[] = [];
    let cur = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQ) {
        if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"') { inQ = false; }
        else { cur += c; }
      } else {
        if (c === ',') { out.push(cur); cur = ''; }
        else if (c === '"') { inQ = true; }
        else { cur += c; }
      }
    }
    out.push(cur);
    return out.map(s => s.trim());
  };
  const headers = parseLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_'));
  return lines.slice(1).map(l => {
    const cells = parseLine(l);
    const row: Record<string, string> = {};
    headers.forEach((h, i) => { row[h] = cells[i] ?? ''; });
    return row;
  });
}

export function CrmImportCard() {
  const { role, mode, dealershipId, userId } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  if (mode !== 'real') return null;
  if (role !== 'manager' && role !== 'admin') return null;
  if (!dealershipId) return null;

  const downloadTemplate = () => {
    const csv = [TEMPLATE_HEADERS.join(','), SAMPLE_ROW.map(v => /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v).join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pullover-deals-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = async (file: File) => {
    setBusy(true);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length === 0) {
        toast.error('No rows found in CSV');
        return;
      }
      if (rows.length > 500) {
        toast.error('Max 500 rows per import. Split your file.');
        return;
      }
      const payload = rows
        .filter(r => r.customer_name && r.vehicle)
        .map(r => {
          const stage = (r.stage || 'new').toLowerCase();
          const priority = (r.priority || 'warm').toLowerCase();
          const val = parseFloat(r.estimated_value || '0');
          return {
            dealership_id: dealershipId,
            rep_id: userId,
            customer_name: r.customer_name.slice(0, 120),
            vehicle: r.vehicle.slice(0, 120),
            phone: r.phone || null,
            email: r.email || null,
            stage: VALID_STAGES.has(stage) ? stage : 'new',
            priority: VALID_PRIORITY.has(priority) ? priority : 'warm',
            estimated_value: isNaN(val) ? 0 : val,
            next_step: r.next_step || null,
            notes: r.notes || '',
            status: 'active',
          };
        });
      if (payload.length === 0) {
        toast.error('Each row needs at least customer_name and vehicle');
        return;
      }
      const { error, count } = await supabase.from('deals').insert(payload, { count: 'exact' });
      if (error) {
        toast.error(`Import failed: ${error.message}`);
        return;
      }
      toast.success(`Imported ${count ?? payload.length} deal${(count ?? payload.length) === 1 ? '' : 's'}`);
    } catch (e: any) {
      toast.error(e?.message ?? 'Could not read CSV');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Upload className="h-4 w-4 text-primary" /> Import deals from CRM
        </CardTitle>
        <CardDescription>
          Bulk-load open ups from DealerSocket, VinSolutions, or any CRM that exports CSV. Each row creates one deal assigned to you — reassign reps from Deal Detail after import.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={downloadTemplate}>
            <FileDown className="h-4 w-4 mr-2" /> Download template
          </Button>
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {busy ? 'Importing…' : 'Upload CSV'}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Required columns: <span className="font-mono">customer_name</span>, <span className="font-mono">vehicle</span>. Optional: phone, email, stage, priority, estimated_value, next_step, notes. Max 500 rows.
        </p>
      </CardContent>
    </Card>
  );
}